// src/services/aeo/formResponses.ts
//
// Every answer to the onboarding Google Form, and what became of it in Oura
// (#4065). The backend reads the form itself and joins it to what it holds, so
// an answer whose script run failed is finally visible from Oura: Google has
// it, Oura does not.
//
// BACKEND CONTRACT (ourteam-backend#5606, staff only):
//
//   GET   /onboarding/form-responses?status=&since=&limit=   -> FormResponseList
//   POST  /onboarding/form-responses/{responseId}/reprocess  -> ReprocessReply
//   POST  /onboarding/unmatched/{submissionId}/dismiss       -> the answer, dismissed (#6454)
//
// Refusals carry one plain sentence in `message` and a code in `errors[0]`:
// 403 forbidden, 404 response_not_found, 409 forms_not_configured,
// 502 forms_access_denied, 502 forms_unavailable, 500 filing_failed.
//
// Until those routes are on an environment the screen runs on the sample store
// at the bottom of this file, the way onboarding.ts did. Production hides the
// screen while the flag is off, so no real user reads sample answers as real.

import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import { isOuraProd } from "@oura/role";
import { assignUnmatchedAnswer, dismissUnmatchedAnswer, type AnswerDismissal } from "./onboarding";

const AEO_BASE = `${import.meta.env.VITE_TALENT_AI_BE_BASE_URL}/api/v1`;
// Its own flag, explicit and absent means OFF: the rest of onboarding can be
// live on an environment before these routes are.
const useFixtures = import.meta.env.VITE_ENABLE_OURA_FORM_RESPONSES_LIVE !== "true";

/** True once the form-responses routes are live on this environment. */
export const isFormResponsesLive = !useFixtures;
/** Whether the screen exists at all: live, or sample data outside production. */
export const FORM_ANSWERS_ON: boolean = isFormResponsesLive || !isOuraProd();

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * filed:        on a client's onboarding record.
 * unmatched:    arrived, but the website matches no client.
 * dismissed:    matched no client and staff set it aside, with a reason (backend #6454).
 * not_received: Google has it, Oura does not. The form's script failed or never ran.
 */
export type FormResponseStatus = "filed" | "unmatched" | "dismissed" | "not_received";

export interface FormResponseAttempt {
    at: string;
    /** `script` is the form's own run, `staff` a fetch from this screen. */
    source: string;
    outcome: string;
    errorCode: string | null;
    /** One plain sentence from the backend. */
    message: string | null;
}

export interface FormResponseItem {
    responseId: string;
    submittedAt: string;
    respondentEmail: string | null;
    website: string | null;
    status: FormResponseStatus;
    /** False for a stored answer whose response is no longer in Google: it cannot be fetched again. */
    inGoogle: boolean;
    /** The stored row. Null until Oura holds the answer. It is the `rowId` the assign route takes. */
    submissionId: string | null;
    businessId: string | null;
    businessName: string | null;
    fileCount: number;
    /** Null when Oura never saw the response at all. */
    lastAttempt: FormResponseAttempt | null;
    /** Who set it aside, when and why; null unless `status` is dismissed. Absent from a backend before #6454. */
    dismissed?: AnswerDismissal | null;
}

export interface FormResponseSummary {
    total: number;
    filed: number;
    unmatched: number;
    /** Absent from a backend before #6454: read it as 0. */
    dismissed?: number;
    notReceived: number;
}

export interface FormResponseList {
    /** Which form was read: "staging" or "production". */
    environment: string;
    /** The whole window, whatever `status` filter was asked for. */
    summary: FormResponseSummary;
    items: FormResponseItem[];
}

export interface FormResponseQuery {
    /** Empty or absent asks for every status. */
    status?: FormResponseStatus[];
    /** YYYY-MM-DD. */
    since: string;
    limit: number;
}

/**
 * A refused read, by what the screen does about it:
 * not_configured (409) is a full-page notice with no retry, unavailable (502,
 * or no reply at all) is an inline error with Try again, forbidden (403) is
 * neither.
 */
export type FormResponsesRead =
    | { ok: true; data: FormResponseList }
    | { ok: false; kind: "not_configured" | "unavailable" | "forbidden"; message: string };

export interface ReprocessReply {
    responseId: string;
    status: FormResponseStatus;
    matched: boolean;
    businessId: string | null;
    businessName: string | null;
    submissionId: string | null;
    /** The answer was already in Oura: nothing was filed twice. */
    alreadyStored: boolean;
}

export type ReprocessResult = { ok: true; data: ReprocessReply } | { ok: false; message: string };
export type AssignResult = { ok: true } | { ok: false; message: string };
export type DismissResult = { ok: true; dismissed: AnswerDismissal } | { ok: false; message: string };

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** The backend's own sentence, or ours when the request never got an answer. Never a code. */
function refused(err: unknown, fallback: string, where: string): { status: number | undefined; message: string } {
    const res = (err as { response?: { status?: number; data?: { message?: unknown } } }).response;
    const message = typeof res?.data?.message === "string" ? res.data.message.trim() : "";
    if (!message) catchErrorLog(`[oura] ${where} failed: ${err}`);
    return { status: res?.status, message: message || fallback };
}

/** Every form answer in the window, newest first. Never throws. */
export async function listFormResponses(query: FormResponseQuery): Promise<FormResponsesRead> {
    if (useFixtures) return mock.list(query);
    try {
        const res = await axios.get(`${AEO_BASE}/onboarding/form-responses`, {
            headers: authHeaders(),
            params: { since: query.since, limit: query.limit, ...(query.status?.length ? { status: query.status.join(",") } : {}) },
        });
        return { ok: true, data: res.data.data as FormResponseList };
    } catch (err) {
        const { status, message } = refused(err, "The form answers could not be read. Try again in a moment.", "listFormResponses");
        return { ok: false, kind: status === 409 ? "not_configured" : status === 403 ? "forbidden" : "unavailable", message };
    }
}

/** Fetch one answer from Google and file it. Safe to repeat: the backend never files one twice. */
export async function reprocessFormResponse(responseId: string): Promise<ReprocessResult> {
    if (useFixtures) return mock.reprocess(responseId);
    try {
        const res = await axios.post(`${AEO_BASE}/onboarding/form-responses/${encodeURIComponent(responseId)}/reprocess`, undefined, { headers: authHeaders() });
        return { ok: true, data: res.data.data as ReprocessReply };
    } catch (err) {
        return { ok: false, message: refused(err, "The answer was not fetched. Try again in a moment.", "reprocessFormResponse").message };
    }
}

/** File an answer that matched no client on the one it belongs to: the assign route from #3977. */
export async function assignFormResponse(submissionId: string, client: { businessId: string; name: string }): Promise<AssignResult> {
    if (useFixtures) return mock.assign(submissionId, client);
    const r = await assignUnmatchedAnswer(submissionId, client.businessId);
    return r.ok ? { ok: true } : { ok: false, message: r.message };
}

/** Set aside an answer that matched no client, with the reason staff give (#6454). */
export async function dismissFormResponse(submissionId: string, reason: string): Promise<DismissResult> {
    if (useFixtures) return mock.dismiss(submissionId, reason);
    const r = await dismissUnmatchedAnswer(submissionId, reason);
    if (!r.ok) return { ok: false, message: r.message };
    return { ok: true, dismissed: r.answer.dismissed ?? { at: new Date().toISOString(), by: null, reason } };
}

// ---------------------------------------------------------------------------
// Sample store, in memory. DELETE once the routes are live everywhere.
// ---------------------------------------------------------------------------

const HOUR = 3_600_000;
const ago = (hours: number) => new Date(Date.now() - hours * HOUR).toISOString();
const delay = <T,>(value: T, ms = 500): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(value), ms));

function sample(
    responseId: string,
    hours: number,
    website: string,
    status: FormResponseStatus,
    more: Partial<FormResponseItem> = {},
): FormResponseItem {
    return {
        responseId,
        submittedAt: ago(hours),
        respondentEmail: `owner@${website}`,
        website,
        status,
        inGoogle: true,
        submissionId: status === "not_received" ? null : `row-${responseId}`,
        businessId: null,
        businessName: null,
        fileCount: 0,
        lastAttempt: null,
        ...more,
    };
}

const scriptRun = (hours: number, outcome: string, message: string | null = null): FormResponseAttempt => ({
    at: ago(hours),
    source: "script",
    outcome,
    errorCode: outcome === "failed" ? "filing_failed" : null,
    message,
});

/** One row per state the screen has to show. The four not-received rows each answer a fetch differently. */
let rows: FormResponseItem[] = [
    sample("sample-files", 3, "ariaskin.example", "not_received", { fileCount: 2, lastAttempt: scriptRun(3, "failed", "The answer arrived but could not be filed.") }),
    sample("sample-unmatched", 20, "newshop.example", "not_received"),
    sample("sample-stored", 30, "luxedental.example", "not_received"),
    sample("sample-refused", 50, "studio.example", "not_received"),
    sample("sample-stray", 70, "blancdental.example", "unmatched", { fileCount: 1, lastAttempt: scriptRun(70, "unmatched") }),
    sample("sample-filed", 96, "nyonyatable.example", "filed", { businessId: "nyonya-table", businessName: "Nyonya Table", lastAttempt: scriptRun(96, "filed") }),
    sample("sample-gone", 200, "pilates.example", "filed", { inGoogle: false, businessId: "pilates-collective", businessName: "The Pilates Collective" }),
];

const staffRun = (outcome: string): FormResponseAttempt => ({ at: new Date().toISOString(), source: "staff", outcome, errorCode: null, message: null });

function patch(responseId: string, change: Partial<FormResponseItem>): FormResponseItem | undefined {
    rows = rows.map((r) => (r.responseId === responseId ? { ...r, ...change } : r));
    return rows.find((r) => r.responseId === responseId);
}

const mock = {
    list(query: FormResponseQuery): Promise<FormResponsesRead> {
        const inWindow = rows.filter((r) => r.submittedAt.slice(0, 10) >= query.since);
        const count = (s: FormResponseStatus) => inWindow.filter((r) => r.status === s).length;
        const items = inWindow.filter((r) => !query.status?.length || query.status.includes(r.status)).slice(0, query.limit);
        return delay({
            ok: true as const,
            data: {
                environment: "staging",
                summary: { total: inWindow.length, filed: count("filed"), unmatched: count("unmatched"), dismissed: count("dismissed"), notReceived: count("not_received") },
                items,
            },
        });
    },
    reprocess(responseId: string): Promise<ReprocessResult> {
        const row = rows.find((r) => r.responseId === responseId);
        if (!row) return delay({ ok: false as const, message: "Google has no answer with that id on this form." });
        if (responseId === "sample-refused") return delay({ ok: false as const, message: "Google did not answer in time. Try again in a moment." }, 900);
        const matched = responseId !== "sample-unmatched";
        const next = patch(responseId, {
            status: matched ? "filed" : "unmatched",
            submissionId: `row-${responseId}`,
            businessId: matched ? "aria-skin-clinic" : null,
            businessName: matched ? "Aria Skin Clinic" : null,
            lastAttempt: staffRun(matched ? "filed" : "unmatched"),
        }) as FormResponseItem;
        return delay(
            {
                ok: true as const,
                data: {
                    responseId,
                    status: next.status,
                    matched,
                    businessId: next.businessId,
                    businessName: next.businessName,
                    submissionId: next.submissionId,
                    alreadyStored: responseId === "sample-stored",
                },
            },
            900,
        );
    },
    dismiss(submissionId: string, reason: string): Promise<DismissResult> {
        const row = rows.find((r) => r.submissionId === submissionId);
        if (!row || row.status !== "unmatched") return delay({ ok: false as const, message: "That answer is no longer waiting. Reload the list." });
        if (!reason.trim()) return delay({ ok: false as const, message: "Say why this answer is dismissed." });
        const dismissed = { at: new Date().toISOString(), by: "You", reason: reason.trim() };
        patch(row.responseId, { status: "dismissed", dismissed });
        return delay({ ok: true as const, dismissed });
    },
    assign(submissionId: string, client: { businessId: string; name: string }): Promise<AssignResult> {
        const row = rows.find((r) => r.submissionId === submissionId);
        if (!row || row.status !== "unmatched") return delay({ ok: false as const, message: "That answer is already filed on a client. Reload the list." });
        patch(row.responseId, { status: "filed", businessId: client.businessId, businessName: client.name, lastAttempt: staffRun("filed") });
        return delay({ ok: true as const });
    },
};
